import { SyncOutlined } from '@ant-design/icons';
import {
  useGetArchivedSubmissionsId,
  useGetArchivedSubmissionsIdDossierToken,
} from '@framework/api-client';
import {
  SubmissionResponsiveCard,
  TitleSubmissionRow,
} from '@framework/common-web';
import { useTranslation } from '@framework/i18n';
import {
  ISODate,
  useBreadcrumbItem,
  useHistory,
  useIdFromUrl,
  type BrandedPath,
} from '@framework/ui';
import { Flex, Space, Spin, Tag, Typography } from 'antd';
import { useState } from 'react';
import { type RouteComponentProps } from 'react-router';
import { DocumentalDossierList } from '../../../../../../backoffice-web/src/client/routes/procedure/single/tab/documental/DocumentalDossierList';
import { PageLayout } from '../../../../ui/layout/PageLayout';
import { NetworkAlertPage } from '../../../errors/network-alert/NetworkAlertPage';
import { LoadingPage } from '../../../loading/LoadingPage';
import { ContentCardWrapper } from '../../components/ContentCardWrapper';
import { ArchivedStateTag } from '../list/archivedSubmissionColumns';

const { Text } = Typography;

interface Params {
  id: string;
}

export interface IArchivePageProps extends RouteComponentProps<Params> {}

export function ArchivePage(props: IArchivePageProps) {
  const [rawId] = useIdFromUrl(props, 'id');
  const id =
    rawId && Number.isInteger(Number(rawId)) ? Number(rawId) : undefined;
  const history = useHistory();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<string>('receipt');

  const {
    data: submission,
    isLoading,
    isFetching,
    error,
  } = useGetArchivedSubmissionsId(id!, { query: { enabled: id != null } });

  const dossierResult = useGetArchivedSubmissionsIdDossierToken(id!, {
    query: { enabled: id != null },
  });

  // Breadcrumbs
  useBreadcrumbItem({ path: '/richieste', title: 'Richieste' });
  useBreadcrumbItem({
    path: '/richieste/archiviate',
    title: 'Archiviate',
  });

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: submission?.code,
  });

  if (isLoading || isFetching) {
    return <LoadingPage />;
  }

  if (error || !id) {
    return (
      <NetworkAlertPage
        message={t(
          'archived-submission.impossibile-caricare-dettaglio-richiesta',
          "Impossibile caricare il dettaglio dell'Istanza archiviata.",
        )}
        error={error}
      />
    );
  }

  return (
    <PageLayout>
      <ContentCardWrapper>
        <SubmissionResponsiveCard
          title={
            <TitleSubmissionRow
              stateTag={
                <ArchivedStateTag color="magenta-inverse">
                  {t(`archived-submissions.status`, 'ARCHIVIATA')}
                </ArchivedStateTag>
              }
              right={<Space></Space>}
            >
              {submission?.procedure?.title ?? ''}
            </TitleSubmissionRow>
          }
          cover={<></>}
          tabList={[
            {
              key: 'receipt',
              label: t('archive-details.ricevuta-invio', 'Ricevuta invio'),
            },
            {
              key: 'dossier',
              disabled: !dossierResult.data?.dossierToken,
              label: (
                <Space size="small">
                  {dossierResult.isLoading && (
                    <Spin
                      size="small"
                      indicator={
                        <SyncOutlined spin aria-label={'Caricamento...'} />
                      }
                    />
                  )}
                  {t('archive-details.fascicolo', 'Fascicolo')}
                </Space>
              ),
            },
          ]}
          activeTabKey={activeTab}
          onTabChange={setActiveTab}
        >
          {/* Ricevuta Invio */}
          {activeTab === 'receipt' && (
            <Flex vertical gap={6}>
              <Space>
                <Text>{t('archive-data.stato', 'Stato:')}</Text>
                <Tag color="magenta-inverse">ARCHIVIATA</Tag>
              </Space>
              <Space>
                <Text>{t('archive-data.autore', 'Autore:')}</Text>
                <Text strong>
                  {submission?.authorName ?? ''}{' '}
                  {submission?.authorFamilyName ?? ''}
                </Text>
              </Space>
              {submission?.sentAt && (
                <Space>
                  <Text>{t('archive-data.data-invio', 'Data invio:')}</Text>
                  <Text strong>
                    <ISODate
                      date={submission?.sentAt}
                      format="DD/MM/YYYY HH:mm"
                    />
                  </Text>
                </Space>
              )}
              <Space>
                <Text>{t('archive-data.codice-invio', 'Codice invio:')}</Text>
                <Text strong>{submission?.code ?? ''}</Text>
              </Space>
              {/* Dati fascicolo e Protocollo */}
              {submission?.dossierCode && (
                <Space>
                  <Text style={{ fontWeight: 'normal' }}>
                    {t('archive-data.numero-fascicolo', 'Numero Fascicolo:')}
                  </Text>
                  <Text strong>{submission.dossierCode}</Text>
                </Space>
              )}
              {submission?.protocolSignature && (
                <Space>
                  <Text>
                    {t('archive-data.numero-protocollo', 'Numero Protocollo:')}
                  </Text>
                  <Text strong>{submission.protocolSignature}</Text>
                </Space>
              )}
              {submission?.subject && (
                <Space>
                  <Text>{t('archive-data.oggetto', 'Oggetto:')}</Text>
                  <Text strong>{submission.subject}</Text>
                </Space>
              )}
            </Flex>
          )}
          {activeTab === 'dossier' && (
            <div>
              <DocumentalDossierList
                dossierId={dossierResult?.data?.dossierId}
                documentalToken={dossierResult?.data?.dossierToken}
                isRoot
              />
            </div>
          )}
        </SubmissionResponsiveCard>
      </ContentCardWrapper>
    </PageLayout>
  );
}
